import { IRoadmap } from "@features/shared/entities/roadmaps";

export const LEARNING_ROADMAP_ID = 5;
export const CLAUDE_ROADMAP_ID = 6;

/**
 * Static fallback selection when the roadmap sequence is unavailable (the
 * /roadmaps/config fetch failed): the learning roadmap, matching the seeded
 * sequence's default recommendation. Kept as a plain constant — the old
 * learning_roadmap / claude_roadmap Amplitude flags that used to pick this are
 * retired; the order and recommendations now live in the admin "Roadmap sequence".
 */
export const resolveFallbackRoadmapId = (roadmaps: IRoadmap[]): number | null => {
    if (roadmaps.some((r) => r.id === LEARNING_ROADMAP_ID)) return LEARNING_ROADMAP_ID;
    const recommended = roadmaps
        .filter((r) => r.recommended)
        .sort((a, b) => a.id - b.id)[0];
    return recommended?.id ?? null;
};

/**
 * Display order for the roadmap selector (Home "Edit" modal + product onboarding).
 *
 * With the roadmap sequence (roadmapKeys from GET /roadmaps/config) the sequence is
 * authoritative for visibility and order, with one twist: the student's recommended
 * roadmap is always moved to the FIRST position — the sequence holds dozens of
 * roadmaps, so the recommendation must not be buried. The user's currently selected
 * roadmap is always appended even when the sequence omits it — hiding a roadmap
 * from the sequence must never hide the roadmap someone is learning on.
 *
 * Static fallback (no sequence — config fetch failed): the trio {learning 5, claude 6,
 * recommended} is pinned above everything else, then the remaining roadmaps by id —
 * the same order the seeded sequence encodes: [5, 6, 1, 2, 3, 4].
 */
export const orderRoadmapsForDisplay = (
    roadmaps: IRoadmap[],
    roadmapKeys?: number[] | null,
    recommendedId?: number | null,
    selectedRoadmapId?: number
): IRoadmap[] => {
    const byId = new Map(roadmaps.map((r) => [r.id, r]));

    if (roadmapKeys && roadmapKeys.length > 0) {
        let ordered = roadmapKeys
            .map((key) => byId.get(key))
            .filter((r): r is IRoadmap => Boolean(r));

        // A sequence that matches nothing fetched (all drafts/deleted) falls
        // through to the static fallback order instead of rendering an empty selector.
        if (ordered.length > 0) {
            const recommended = recommendedId ? byId.get(recommendedId) : undefined;
            if (recommended) {
                ordered = [recommended, ...ordered.filter((r) => r.id !== recommended.id)];
            }

            if (selectedRoadmapId && !ordered.some((r) => r.id === selectedRoadmapId)) {
                const selected = byId.get(selectedRoadmapId);
                if (selected) ordered.push(selected);
            }

            return ordered;
        }
    }

    const sorted = [...roadmaps].sort((a, b) => a.id - b.id);
    const flagRecommendedId = sorted.find((r) => r.recommended)?.id ?? null;

    const seen = new Set<number>();
    const pinned: IRoadmap[] = [];
    for (const id of [recommendedId ?? null, LEARNING_ROADMAP_ID, CLAUDE_ROADMAP_ID, flagRecommendedId]) {
        if (id === null || seen.has(id)) continue;
        const roadmap = byId.get(id);
        if (roadmap) {
            pinned.push(roadmap);
            seen.add(id);
        }
    }

    const rest = sorted.filter((r) => !seen.has(r.id));
    return [...pinned, ...rest];
};
